import { createHigherOrderComponent } from '@wordpress/compose';
import { Fragment, useState } from '@wordpress/element';
import { BlockControls } from '@wordpress/block-editor';
import {
	ToolbarGroup,
	ToolbarButton,
	Modal,
	Button,
	TextControl,
	TextareaControl,
	CheckboxControl,
} from '@wordpress/components';
import { __ } from '@wordpress/i18n';
import { select, dispatch } from '@wordpress/data';
import api from '../functions/api';
import html2canvas from 'html2canvas-pro';

// The function to generate the example from the block data
function getInnerBlockExample( block ) {
	const innerBlockExample = {
		name: block.name,
		attributes: block.attributes,
	};
	if ( block.innerBlocks && block.innerBlocks.length > 0 ) {
		innerBlockExample.innerBlocks =
			block.innerBlocks.map( getInnerBlockExample );
	}
	return innerBlockExample;
}

function getBlockJsonExample( block ) {
	const example = {
		attributes: block.attributes,
	};

	if ( block.innerBlocks && block.innerBlocks.length > 0 ) {
		example.innerBlocks = block.innerBlocks.map( getInnerBlockExample );
	}

	return example;
}

const withExampleButton = createHigherOrderComponent( ( BlockEdit ) => {
	return ( props ) => {
		const [ isModalOpen, setIsModalOpen ] = useState( false );
		const [ exampleJson, setExampleJson ] = useState( '' );
		const [ isVariationModalOpen, setIsVariationModalOpen ] =
			useState( false );
		const [ variationName, setVariationName ] = useState( '' );
		const [ variationTitle, setVariationTitle ] = useState( '' );
		const [ variationJson, setVariationJson ] = useState( '' );
		const [ isHtmlModalOpen, setIsHtmlModalOpen ] = useState( false );
		const [ htmlContent, setHtmlContent ] = useState( '' );
		const [ isUploadModalOpen, setIsUploadModalOpen ] = useState( false );
		const [ uploadName, setUploadName ] = useState( '' );
		const [ uploadId, setUploadId ] = useState( '' );
		const [ uploadImage, setUploadImage ] = useState( '' );
		const [ uploadType, setUploadType ] = useState( '' );
		const [ uploadContent, setUploadContent ] = useState( '' );
		const [ uploadKeyId, setUploadKeyId ] = useState( '' );
		const [ uploadExample, setUploadExample ] = useState( '' );
		const [ uploadVariations, setUploadVariations ] = useState( '' );
		const [ isDefaultExample, setIsDefaultExample ] = useState( false );

		const openModal = () => {
			const selectedBlock =
				select( 'core/block-editor' ).getSelectedBlock();
			if ( selectedBlock ) {
				const example = getBlockJsonExample( selectedBlock );
				setExampleJson( JSON.stringify( example, null, 2 ) );
				setIsModalOpen( true );
			}
		};
		const closeModal = () => setIsModalOpen( false );

		const copyAndClose = () => {
			navigator.clipboard.writeText( exampleJson ).then(
				() => {
					const { createNotice } = dispatch( 'core/notices' );
					createNotice(
						'success',
						__(
							'Block example copied to clipboard.',
							'alpha-blocks'
						),
						{
							isDismissible: true,
							type: 'snackbar',
						}
					);
				},
				() => {
					const { createNotice } = dispatch( 'core/notices' );
					createNotice(
						'error',
						__(
							'Could not copy block example to clipboard.',
							'alpha-blocks'
						),
						{
							isDismissible: true,
							type: 'snackbar',
						}
					);
				}
			);
			closeModal();
		};

		const openVariationModal = () => setIsVariationModalOpen( true );
		const closeVariationModal = () => {
			setIsVariationModalOpen( false );
			setVariationJson( '' );
			setVariationName( '' );
			setVariationTitle( '' );
		};

		const generateVariation = () => {
			const selectedBlock =
				select( 'core/block-editor' ).getSelectedBlock();
			if ( selectedBlock ) {
				const variation = {
					name: variationName,
					title: variationTitle,
					attributes: selectedBlock.attributes,
					innerBlocks:
						getBlockJsonExample( selectedBlock ).innerBlocks,
				};
				setVariationJson( JSON.stringify( variation, null, 2 ) );
			}
		};

		const copyVariationAndClose = () => {
			navigator.clipboard.writeText( variationJson ).then(
				() => {
					const { createNotice } = dispatch( 'core/notices' );
					createNotice(
						'success',
						__( 'Variation copied to clipboard.', 'alpha-blocks' ),
						{
							isDismissible: true,
							type: 'snackbar',
						}
					);
				},
				() => {
					const { createNotice } = dispatch( 'core/notices' );
					createNotice(
						'error',
						__(
							'Could not copy variation to clipboard.',
							'alpha-blocks'
						),
						{
							isDismissible: true,
							type: 'snackbar',
						}
					);
				}
			);
			closeVariationModal();
		};

		const openHtmlModal = () => {
			const selectedBlock =
				select( 'core/block-editor' ).getSelectedBlock();
			if ( selectedBlock ) {
				const html = wp.blocks.serialize( selectedBlock );
				setHtmlContent( html );
				setIsHtmlModalOpen( true );
			}
		};

		const closeHtmlModal = () => setIsHtmlModalOpen( false );

		const copyHtmlAndClose = () => {
			navigator.clipboard.writeText( htmlContent ).then(
				() => {
					const { createNotice } = dispatch( 'core/notices' );
					createNotice(
						'success',
						__( 'HTML copied to clipboard.', 'alpha-blocks' ),
						{
							isDismissible: true,
							type: 'snackbar',
						}
					);
				},
				() => {
					const { createNotice } = dispatch( 'core/notices' );
					createNotice(
						'error',
						__(
							'Could not copy HTML to clipboard.',
							'alpha-blocks'
						),
						{
							isDismissible: true,
							type: 'snackbar',
						}
					);
				}
			);
			closeHtmlModal();
		};

		const openUploadModal = async () => {
			const selectedBlock =
				select( 'core/block-editor' ).getSelectedBlock();
			if ( selectedBlock ) {
				const { name, clientId, attributes } = selectedBlock;
				const content = wp.blocks.serialize( selectedBlock );
				const example = getBlockJsonExample( selectedBlock );

				const element = document.querySelector(
					`#block-${ clientId }`
				);
				const { width, height } = element.getBoundingClientRect();
				const canvas = await html2canvas( element, {
					width,
					height,
					// scale: 2,
					useCORS: true,
					logging: true,
					// windowWidth: width,
					// windowHeight: height,
				} );
				const imageUrl = canvas.toDataURL( 'image/png' );

				const type = name.split( '/' ).pop();

				setUploadName( name );
				setUploadId( clientId );
				setUploadImage( imageUrl );
				setUploadType( type );
				setUploadContent( content );
				setUploadExample( JSON.stringify( example, null, 2 ) );
				setUploadVariations( '' );
				setIsUploadModalOpen( true );
			}
		};

		const closeUploadModal = () => {
			setIsUploadModalOpen( false );
			setUploadExample( '' );
			setUploadVariations( '' );
			setUploadKeyId( '' );
			setIsDefaultExample( false );
		};

		const uploadBlockData = () => {
			const data = {
				name: uploadName,
				id: uploadId,
				image: uploadImage,
				type: uploadType,
				content: uploadContent,
				key_id: uploadKeyId,
				example: uploadExample,
				variations: uploadVariations,
				is_default_example: isDefaultExample,
			};

			api.post( 'upload_block_data', data ).then(
				() => {
					const { createNotice } = dispatch( 'core/notices' );
					createNotice(
						'success',
						__( 'Block data uploaded.', 'alpha-blocks' ),
						{
							isDismissible: true,
							type: 'snackbar',
						}
					);
					closeUploadModal();
				},
				() => {
					const { createNotice } = dispatch( 'core/notices' );
					createNotice(
						'error',
						__( 'Could not upload block data.', 'alpha-blocks' ),
						{
							isDismissible: true,
							type: 'snackbar',
						}
					);
				}
			);
		};

		return (
			<Fragment>
				<BlockControls>
					<ToolbarGroup>
						<ToolbarButton
							icon="clipboard"
							label={ __( 'Copy Example', 'alpha-blocks' ) }
							onClick={ openModal }
						/>
						<ToolbarButton
							icon="plus"
							label={ __( 'Create Variation', 'alpha-blocks' ) }
							onClick={ openVariationModal }
						/>
						<ToolbarButton
							icon="html"
							label={ __( 'Copy HTML', 'alpha-blocks' ) }
							onClick={ openHtmlModal }
						/>
						<ToolbarButton
							icon="upload"
							label={ __( 'Upload Block Data', 'alpha-blocks' ) }
							onClick={ openUploadModal }
						/>
					</ToolbarGroup>
				</BlockControls>
				{ isModalOpen && (
					<Modal
						title={ __( 'Block Example', 'alpha-blocks' ) }
						onRequestClose={ closeModal }
					>
						<pre>
							<code>{ exampleJson }</code>
						</pre>
						<Button isPrimary onClick={ copyAndClose }>
							{ __( 'Copy', 'alpha-blocks' ) }
						</Button>
					</Modal>
				) }
				{ isHtmlModalOpen && (
					<Modal
						title={ __( 'Block HTML', 'alpha-blocks' ) }
						onRequestClose={ closeHtmlModal }
					>
						<pre>
							<code>{ htmlContent }</code>
						</pre>
						<Button isPrimary onClick={ copyHtmlAndClose }>
							{ __( 'Copy', 'alpha-blocks' ) }
						</Button>
					</Modal>
				) }
				{ isVariationModalOpen && (
					<Modal
						title={ __( 'Create Variation', 'alpha-blocks' ) }
						onRequestClose={ closeVariationModal }
					>
						<TextControl
							label={ __( 'Name', 'alpha-blocks' ) }
							value={ variationName }
							onChange={ setVariationName }
						/>
						<TextControl
							label={ __( 'Title', 'alpha-blocks' ) }
							value={ variationTitle }
							onChange={ setVariationTitle }
						/>
						<Button isPrimary onClick={ generateVariation }>
							{ __( 'Generate', 'alpha-blocks' ) }
						</Button>
						{ variationJson && (
							<Fragment>
								<pre>
									<code>{ variationJson }</code>
								</pre>
								<Button
									isPrimary
									onClick={ copyVariationAndClose }
								>
									{ __( 'Copy', 'alpha-blocks' ) }
								</Button>
							</Fragment>
						) }
					</Modal>
				) }
				{ isUploadModalOpen && (
					<Modal
						title={ __( 'Upload Block Data', 'alpha-blocks' ) }
						onRequestClose={ closeUploadModal }
						style={ { width: '80vw' } }
					>
						<TextControl
							label={ __( 'Name', 'alpha-blocks' ) }
							value={ uploadName }
							onChange={ setUploadName }
						/>
						<TextControl
							label={ __( 'ID', 'alpha-blocks' ) }
							value={ uploadId }
							onChange={ setUploadId }
						/>
						<TextControl
							label={ __( 'Image URL', 'alpha-blocks' ) }
							value={ uploadImage }
							onChange={ setUploadImage }
						/>
						{ uploadImage && (
							<img
								src={ uploadImage }
								style={ {
									maxWidth: '100%',
									marginTop: '10px',
								} }
							/>
						) }
						<TextControl
							label={ __( 'Type', 'alpha-blocks' ) }
							value={ uploadType }
							onChange={ setUploadType }
						/>
						<TextareaControl
							label={ __( 'Content', 'alpha-blocks' ) }
							value={ uploadContent }
							onChange={ setUploadContent }
							rows="10"
						/>
						<TextareaControl
							label={ __( 'Block Example', 'alpha-blocks' ) }
							value={ uploadExample }
							onChange={ setUploadExample }
							rows="10"
						/>
						<TextareaControl
							label={ __( 'Block Variations', 'alpha-blocks' ) }
							value={ uploadVariations }
							onChange={ setUploadVariations }
							rows="10"
						/>
						<CheckboxControl
							label={ __(
								'Is it default example',
								'alpha-blocks'
							) }
							checked={ isDefaultExample }
							onChange={ setIsDefaultExample }
						/>
						<TextControl
							label={ __( 'Key ID', 'alpha-blocks' ) }
							value={ uploadKeyId }
							onChange={ setUploadKeyId }
						/>
						<Button isPrimary onClick={ uploadBlockData }>
							{ __( 'Upload', 'alpha-blocks' ) }
						</Button>
					</Modal>
				) }
				<BlockEdit { ...props } />
			</Fragment>
		);
	};
}, 'withExampleButton' );

export default withExampleButton;
